import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type Stripe from "stripe";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createStripeClient, getStripeErrorMessage, type StripeEnv } from "@/lib/stripe.server";
import { isTrialEligiblePrice } from "@/lib/payments/trial-eligibility";

const ID_RE = /^[a-zA-Z0-9_-]+$/;

const checkoutInputSchema = z.object({
  priceId: z.string().regex(ID_RE, "Invalid price id"),
  quantity: z.number().int().positive().optional(),
  // customerEmail / userId are accepted for backwards compatibility with
  // existing callers but are IGNORED server-side: both are always taken from
  // the authenticated session so a caller can never attribute a paid
  // subscription (or a trial) to somebody else's account.
  customerEmail: z.string().email().optional(),
  userId: z.string().regex(ID_RE, "Invalid user id").optional(),
  returnUrl: z.string().url(),
  environment: z.enum(["sandbox", "live"]),
  trialDays: z.number().int().positive().optional(),
  metadata: z.record(z.string()).optional(),
});

export type CreateCheckoutSessionInput = z.infer<typeof checkoutInputSchema>;
/**
 * How long *this* user's trial would be if they started one right now. The UI
 * uses it for copy only ("30/60 dagar gratis"); the real value applied to
 * Stripe is recomputed inside createCheckoutSession.
 */
export const getMyTrialDaysFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<{ trialDays: number }> => {
    const { resolveTrialDays } = await import("@/lib/payments/trial-days.server");
    const trialDays = await resolveTrialDays(context.userId, data.environment);
    return { trialDays };
  });


/**
 * Find (or create) a Stripe Customer that always carries `metadata.userId`,
 * so webhooks and the dashboard can reconcile subscriptions to app users
 * without relying on email (which can change). Also backfills a missing
 * email on legacy customers so Stripe can deliver receipts.
 */
async function resolveOrCreateCustomer(
  stripe: Stripe,
  { userId, email }: { userId?: string; email?: string },
): Promise<{ id: string; email: string | null }> {
  if (userId) {
    if (!ID_RE.test(userId)) {
      throw new Error("Invalid user id");
    }
    const bySearch = await stripe.customers.search({
      query: `metadata['userId']:'${userId}'`,
      limit: 1,
    });
    const found = bySearch.data[0];
    if (found) {
      // Receipts require an email on the Customer — backfill legacy rows.
      if (!found.email && email) {
        const updated = await stripe.customers.update(found.id, { email });
        return { id: updated.id, email: updated.email ?? email };
      }
      return { id: found.id, email: found.email ?? null };
    }
  }

  if (email) {
    const byEmail = await stripe.customers.list({ email, limit: 1 });
    const existing = byEmail.data[0];
    if (existing) {
      if (userId && existing.metadata?.userId !== userId) {
        await stripe.customers.update(existing.id, { metadata: { ...existing.metadata, userId } });
      }
      return { id: existing.id, email: existing.email ?? email };
    }
  }

  const created = await stripe.customers.create({
    email,
    metadata: userId ? { userId } : undefined,
  });
  return { id: created.id, email: created.email ?? email ?? null };
}


/**
 * Create a Stripe embedded Checkout Session and return its client secret.
 * Requires an authenticated session: the Stripe Customer and all userId
 * metadata are derived from the caller's own token, never from the request
 * body, so a checkout can only ever affect the caller's own account.
 */
export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => checkoutInputSchema.parse(input))
  .handler(async ({ data, context }): Promise<{ clientSecret: string } | { error: string }> => {
    try {
      // Authoritative identity — client-supplied userId/customerEmail ignored.
      const authUserId = context.userId;
      const authEmail =
        typeof context.claims?.email === "string" ? context.claims.email : undefined;

      const stripe = createStripeClient(data.environment as StripeEnv);

      const priceList = await stripe.prices.list({ lookup_keys: [data.priceId], expand: ["data.product"] });
      const price = priceList.data[0];
      if (!price) {
        return { error: `Plan "${data.priceId}" isn't available right now. Please contact support.` };
      }

      const mode: "subscription" | "payment" = price.recurring ? "subscription" : "payment";
      const customer = await resolveOrCreateCustomer(stripe, {
        userId: authUserId,
        email: authEmail,
      });

      // Strip any client-supplied `userId` key so it can't override ours.
      const safeMetadata = { ...(data.metadata || {}) };
      delete safeMetadata.userId;

      // Only return to Tack's own origins — a caller-supplied external URL
      // would let an attacker bounce the buyer (and the Stripe
      // {CHECKOUT_SESSION_ID} placeholder) to a phishing page after payment.
      const { safeReturnUrl } = await import("@/lib/payments/subscription-management.server");
      const checkedReturnUrl = await safeReturnUrl(data.returnUrl);
      if (!checkedReturnUrl) {
        return { error: "Invalid return URL." };
      }

      const baseParams: Record<string, unknown> = {
        ui_mode: "embedded_page",
        mode,
        customer: customer.id,
        line_items: [{ price: price.id, quantity: data.quantity ?? 1 }],
        return_url: checkedReturnUrl,
        metadata: { ...safeMetadata, userId: authUserId },
        // Tack is the merchant of record. Stripe Tax calculates and collects
        // VAT; registration, filing and remittance are ours. Tax ID collection
        // renders the "I'm a business" VAT-number field (EU reverse charge) and
        // is only possible with managed payments OFF. Note Stripe hides that
        // field for buyers whose billing country matches ours (SE): a Swedish
        // VAT number cannot change domestic Swedish VAT.
        automatic_tax: { enabled: true },
        billing_address_collection: "required",
        customer_update: { address: "auto", name: "auto" },
        // Optional, never mandatory: "if_supported" would block consumers and
        // not-yet-registered sole traders from completing checkout at all, and a
        // Swedish VAT number does not change domestic Swedish VAT anyway.
        tax_id_collection: { enabled: true, required: "never" },
        // Catalog is SEK-only for a Sweden-first launch. Adaptive Pricing would
        // otherwise show a "Choose currency" picker with a converted amount
        // (plus a 4% conversion fee) to visitors detected outside Sweden.
        adaptive_pricing: { enabled: false },
      };

      if (mode === "subscription") {
        // Trial length is decided server-side: a client can only ask for a
        // trial, never dictate its length. Base 30 days for first-time
        // subscribers, doubled to 60 when the account signed up through a
        // referral code (the invitee half of the referral programme).
        //
        // The free trial belongs to Solo only. Standard/Growth include a
        // larger SMS pool that costs us real money per message, so those tiers
        // always start as paid subscriptions.
        const { resolveTrialDays } = await import("@/lib/payments/trial-days.server");
        const trialDays =
          data.trialDays && isTrialEligiblePrice(data.priceId)
            ? await resolveTrialDays(authUserId, data.environment as StripeEnv)
            : 0;

        baseParams.subscription_data = {
          metadata: { userId: authUserId },
          ...(trialDays > 0 ? { trial_period_days: trialDays } : {}),
        };

      } else {
        const product = price.product as Stripe.Product | string;
        const productName =
          typeof product === "string"
            ? (await stripe.products.retrieve(product)).name
            : product.name;
        const receiptEmail = authEmail ?? customer.email ?? undefined;
        baseParams.payment_intent_data = {
          description: productName,
          ...(receiptEmail ? { receipt_email: receiptEmail } : {}),
        };
        // Business buyers need a proper VAT invoice document, not just a receipt.
        baseParams.invoice_creation = { enabled: true };
      }


      const session = await stripe.checkout.sessions.create(
        baseParams as Stripe.Checkout.SessionCreateParams,
      );

      if (!session.client_secret) {
        return { error: "Stripe did not return a checkout client secret." };
      }

      return { clientSecret: session.client_secret };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

const localizedPricesInputSchema = z.object({
  lookupKeys: z.array(z.string().regex(ID_RE)).min(1),
  environment: z.enum(["sandbox", "live"]),
});

export type LocalizedPriceEntry = {
  amountMinor: number;
  currency: string;
  formatted: string;
};

/** Strip trailing zero-decimals: "$399.00" -> "$399", "3 990,00 kr" -> "3 990 kr". */
function stripZeroDecimals(s: string): string {
  return s.replace(/([.,])00(?!\d)/g, "");
}

function formatAmount(amountMinor: number, currency: string): string {
  try {
    const formatted = new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountMinor / 100);
    return stripZeroDecimals(formatted);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Fetch the catalog prices (all SEK, tax-exclusive) by lookup_key. Replaces
 * Paddle's client-side PricePreview() — Stripe's catalog is single-currency
 * for this launch (Sweden-only), so no per-visitor localization is needed.
 */
export const getLocalizedPrices = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => localizedPricesInputSchema.parse(input))
  .handler(async ({ data }): Promise<Record<string, LocalizedPriceEntry> | { error: string }> => {
    try {
      const stripe = createStripeClient(data.environment as StripeEnv);
      const prices = await stripe.prices.list({
        lookup_keys: data.lookupKeys,
        active: true,
        expand: ["data.product"],
      });


      const out: Record<string, LocalizedPriceEntry> = {};
      for (const price of prices.data) {
        if (!price.lookup_key || price.unit_amount == null) continue;
        out[price.lookup_key] = {
          amountMinor: price.unit_amount,
          currency: price.currency,
          formatted: formatAmount(price.unit_amount, price.currency),
        };
      }
      return out;
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });
